/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'

import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {
    API_ERROR_MESSAGE,
    EINSTEIN_RECOMMENDERS,
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ADDED_TO_WISHLIST,
    TOAST_MESSAGE_REMOVED_FROM_WISHLIST
} from '@salesforce/retail-react-app/app/constants'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import useEinstein from '../../hooks/use-einstein'
import ProductTile, {Skeleton} from '../product-tile'
import {
    bodyStyle,
    containerStyle,
    headingStyle,
    iconButtonLeftHoverStyle,
    iconButtonLeftStyle,
    iconButtonRightHoverStyle,
    iconButtonRightStyle,
    recBoxStyle,
    recBoxStyleForAddToCart,
    scrollContainerStyle,
    skeletonBoxStyle,
    skeletonForAddToCart,
    skeletonStyle,
    arrowContainerLeftStyle,
    arrowContainerRightStyle
} from './index.style'
import {RightArrow, RightArrowHover} from '../icons'

/**
 * A component for fetching and rendering product recommendations from the Einstein API
 * by a zone or a recommender name.
 */
const RecommendedProducts = ({
    zone,
    recommender,
    products,
    title,
    fetchKey,
    shouldFetch,
    isFromAddToCart = false,
    ...props
}) => {
    const {
        isLoading,
        recommendations,
        getZoneRecommendations,
        getRecommendations,
        getRecentlyViewed,
        sendClickReco,
        sendViewReco
    } = useEinstein()

    const ref = useRef()
    const scrollContainerRef = useRef()
    const isOnScreen = useIntersectionObserver(ref, {useOnce: true})
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const checkScrollPosition = () => {
        if (scrollContainerRef.current) {
            const {scrollLeft, scrollWidth, clientWidth} = scrollContainerRef.current
            setCanScrollLeft(scrollLeft > 0)
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 3)
        }
    }

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            const scrollAmount = scrollContainerRef.current.clientWidth * 0.25
            scrollContainerRef.current.scrollBy({
                left: -scrollAmount,
                behavior: 'smooth'
            })
        }
    }

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            const scrollAmount = scrollContainerRef.current.clientWidth * 0.25
            scrollContainerRef.current.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
            })
        }
    }

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current
        if (scrollContainer) {
            checkScrollPosition()
            scrollContainer.addEventListener('scroll', checkScrollPosition)
            const resizeObserver = new ResizeObserver(checkScrollPosition)
            resizeObserver.observe(scrollContainer)
            return () => {
                scrollContainer.removeEventListener('scroll', checkScrollPosition)
                resizeObserver.disconnect()
            }
        }
    }, [recommendations])

    useEffect(() => {
        if (zone) {
            getZoneRecommendations(zone, products)
        } else if (recommender === EINSTEIN_RECOMMENDERS.PDP_MIGHT_ALSO_LIKE) {
            getRecommendations(recommender, products)
        } else if (recommender === EINSTEIN_RECOMMENDERS.PDP_RECENTLY_VIEWED) {
            getRecentlyViewed(recommender)
        }
    }, [zone, recommender, fetchKey])

    useEffect(() => {
        if (isOnScreen && recommendations !== undefined && recommendations?.recs !== undefined) {
            sendViewReco(
                {
                    recommenderName: recommendations.recommenderName,
                    __recoUUID: recommendations.recoUUID
                },
                recommendations?.recs.map((rec) => ({id: rec.id}))
            )
        }
    }, [isOnScreen, recommendations])

    const newProducts = recommendations?.recs || []

    if (!isLoading && newProducts.length === 0) {
        return null
    }

    return (
        <Box sx={containerStyle}>
            <Text sx={headingStyle}>{title}</Text>
            <Box sx={bodyStyle}>
                {!isLoading && canScrollLeft && (
                    <Box sx={arrowContainerLeftStyle} onClick={scrollLeft}>
                        <RightArrow sx={iconButtonLeftStyle} />
                        <RightArrowHover sx={iconButtonLeftHoverStyle} />
                    </Box>
                )}
                {!isLoading && canScrollRight && (
                    <Box sx={arrowContainerRightStyle} onClick={scrollRight}>
                        <RightArrow sx={iconButtonRightStyle} />
                        <RightArrowHover sx={iconButtonRightHoverStyle} />
                    </Box>
                )}
                <Flex ref={scrollContainerRef} sx={scrollContainerStyle}>
                    {isLoading
                        ? Array.from({length: newProducts?.length})?.map((_, idx) => (
                              <Box
                                  key={idx}
                                  sx={isFromAddToCart ? skeletonForAddToCart : skeletonBoxStyle}
                              >
                                  <Skeleton />
                              </Box>
                          ))
                        : newProducts?.map((rec) => (
                              <Box
                                  key={rec?.id}
                                  sx={isFromAddToCart ? recBoxStyleForAddToCart : recBoxStyle}
                              >
                                  <ProductTile
                                      ShowQuickView={true}
                                      product={rec}
                                      onClick={() =>
                                          sendClickReco(
                                              {
                                                  recommenderName: recommendations.recommenderName,
                                                  __recoUUID: recommendations.recoUUID
                                              },
                                              rec
                                          )
                                      }
                                  />
                              </Box>
                          ))}
                </Flex>
            </Box>
        </Box>
    )
}

RecommendedProducts.propTypes = {
    title: PropTypes.any,
    zone: PropTypes.string,
    recommender: PropTypes.string,
    products: PropTypes.arrayOf(PropTypes.object),
    shouldFetch: PropTypes.func,
    isFromAddToCart: PropTypes.func
}

export default RecommendedProducts
